import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveFooter,
  ResponsiveHeader,
} from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { BankIcon, DocumentsIcon } from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import ManualContactModal from '../ui/ManualContactModal';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import { SubHeadline } from '../ui/Typography';
import {
  autoSyncDistributionForAsset,
  removeAssetDistribution,
  type DistributionMap,
} from '../../utils/distributionUtils';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface Beneficiary {
  contactId: string;
  name: string;
}

interface OtherFinancialAsset {
  id: string;
  type: 'life_insurance' | 'annuity' | 'trust' | 'business' | 'other';
  name: string;
  institution?: string;
  policyNumber?: string;
  beneficiaries?: Beneficiary[];
  notes?: string;
  documents?: AppDocument[];
}

const ASSET_TYPES = [
  {
    key: 'life_insurance' as const,
    label: 'Life Insurance',
    description: 'Life insurance policies and death benefits',
    Icon: BankIcon,
  },
  {
    key: 'annuity' as const,
    label: 'Annuities',
    description: 'Fixed or variable annuity contracts',
    Icon: BankIcon,
  },
  {
    key: 'trust' as const,
    label: 'Trust Funds',
    description: 'Trust accounts and beneficiary arrangements',
    Icon: BankIcon,
  },
  {
    key: 'business' as const,
    label: 'Business Interests',
    description: 'Business ownership and partnerships',
    Icon: BankIcon,
  },
  {
    key: 'other' as const,
    label: 'Other Assets',
    description: 'Other financial assets and investments',
    Icon: BankIcon,
  },
];

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame189: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();
  const [assets, setAssets] = useState<OtherFinancialAsset[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showManualContactModal, setShowManualContactModal] = useState(false);
  const [selectedType, setSelectedType] = useState<OtherFinancialAsset['type'] | null>(null);
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [contacts, setContacts] = useState<UnifiedContact[]>([]);

  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [beneficiaryIds, setBeneficiaryIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  useEffect(() => {
    loadContacts();

    if (session?.taskProgress?.financial_assets?.answers) {
      const savedAssets = session.taskProgress.financial_assets.answers.otherFinancialAssets || [];
      if (savedAssets.length > 0) {
        const normalizedAssets = savedAssets.map((asset: OtherFinancialAsset) => ({
          ...asset,
          documents: normalizeDocuments(asset.documents || []),
        }));

        console.log('[Frame189] Loading assets from session:', normalizedAssets);
        setAssets(normalizedAssets);
        return;
      }
    }

    try {
      const backupData = localStorage.getItem('other-financial-assets-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (data.length > 0) {
          const normalizedAssets = data.map((asset: OtherFinancialAsset) => ({
            ...asset,
            documents: normalizeDocuments(asset.documents || []),
          }));

          console.log('[Frame189] Loading assets from localStorage backup:', normalizedAssets);
          setAssets(normalizedAssets);
        }
      }
    } catch (error) {
      console.error('[Frame189] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.financial_assets]);

  const loadContacts = async () => {
    try {
      const allContacts = await unifiedContactService.getAllContacts();
      setContacts(allContacts);
      console.log(`[Frame189] Loaded ${allContacts.length} contacts for beneficiary selection`);
    } catch (error) {
      console.error('[Frame189] Error loading contacts:', error);
    }
  };

  const handleManualContactSubmit = async (contactData: { firstName: string; lastName: string; email?: string; phone?: string }) => {
    try {
      const newContact = await unifiedContactService.createContact({
        firstName: contactData.firstName.trim(),
        lastName: contactData.lastName.trim(),
        email: contactData.email?.trim() || '',
        phone: contactData.phone?.trim(),
        relationship: 'beneficiary',
        source: 'manual',
        notes: 'Added during beneficiary assignment'
      });

      const allContacts = await unifiedContactService.getAllContacts(true);
      setContacts(allContacts);

      setBeneficiaryIds(prev => {
        const firstEmptyIndex = prev.findIndex(id => !id);
        if (firstEmptyIndex >= 0) {
          const updated = [...prev];
          updated[firstEmptyIndex] = newContact.id;
          return updated;
        }
        return [...prev, newContact.id];
      });

      setShowManualContactModal(false);
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };

      if (err.code === 'DUPLICATE_CONTACT' && contactData.email) {
        const allContacts = await unifiedContactService.getAllContacts(true);
        setContacts(allContacts);

        const existing = allContacts.find(
          c => c.email && c.email.toLowerCase() === contactData.email!.trim().toLowerCase()
        );

        if (existing) {
          setBeneficiaryIds(prev => {
            const firstEmptyIndex = prev.findIndex(id => !id);
            if (firstEmptyIndex >= 0) {
              const updated = [...prev];
              updated[firstEmptyIndex] = existing.id;
              return updated;
            }
            return [...prev, existing.id];
          });

          setShowManualContactModal(false);
          return;
        }
      }

      throw error;
    }
  };

  const persistAssets = async (updatedAssets: OtherFinancialAsset[]) => {
    try {
      localStorage.setItem('other-financial-assets-backup', JSON.stringify(updatedAssets));
      localStorage.setItem(
        'other-financial-assets-data-backup',
        JSON.stringify({
          otherFinancialAssets: updatedAssets,
          lastUpdated: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.error('[Frame189] Failed to save to backup:', error);
    }

    await updateTaskProgress('financial_assets', { otherFinancialAssets: updatedAssets });
    setAssets(updatedAssets);
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame189] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const resetForm = () => {
    setName('');
    setInstitution('');
    setPolicyNumber('');
    setBeneficiaryIds([]);
    setNotes('');
    setDocuments([]);
    setEditingAssetId(null);
    setIsUploadingDocuments(false);
  };

  const handleOpenModal = (type: OtherFinancialAsset['type']) => {
    resetForm();
    setSelectedType(type);
    setShowModal(true);
  };

  const handleEditAsset = (asset: OtherFinancialAsset) => {
    setSelectedType(asset.type);
    setName(asset.name);
    setInstitution(asset.institution || '');
    setPolicyNumber(asset.policyNumber || '');
    setBeneficiaryIds(asset.beneficiaries?.map((b) => b.contactId) || []);
    setNotes(asset.notes || '');
    setEditingAssetId(asset.id);
    setDocuments(normalizeDocuments(asset.documents || []));
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedType || !name || !institution || !policyNumber) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const beneficiaryData = beneficiaryIds
        .map((id) => {
          const contact = contacts.find((c) => c.id === id);
          return contact
            ? {
                contactId: id,
                name: `${contact.firstName} ${contact.lastName}`,
              }
            : null;
        })
        .filter((b): b is Beneficiary => b !== null);

      const assetData: OtherFinancialAsset = {
        id: editingAssetId || Date.now().toString(),
        type: selectedType,
        name: name.trim(),
        institution: institution.trim() || undefined,
        policyNumber: policyNumber.trim() || undefined,
        beneficiaries: beneficiaryData.length > 0 ? beneficiaryData : undefined,
        notes: notes.trim() || undefined,
        documents,
      };

      const updatedAssets = editingAssetId
        ? assets.map((a) => (a.id === editingAssetId ? assetData : a))
        : [...assets, assetData];

      // Sync distribution record: recalculate equal-split percentages for all
      // current beneficiaries. Runs for both new and edited assets.
      const currentDist = (session.taskProgress?.assetDistribution?.answers?.distributions ?? {}) as DistributionMap;
      const syncedDist = autoSyncDistributionForAsset(currentDist, assetData.id, beneficiaryData);
      await updateTaskProgress('assetDistribution', { distributions: syncedDist });

      await persistAssets(updatedAssets);
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('[Frame189] Failed to save asset:', error);
      alert('Failed to save asset. Please try again.');
    }
  };

  const handleDeleteAsset = async (assetId: string) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    const assetToDelete = assets.find((a) => a.id === assetId);
    const attachedDocs = assetToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame189] Failed to delete document while deleting asset:', error);
      }
    }

    const updatedAssets = assets.filter((a) => a.id !== assetId);

    // Cascade: remove distribution record for the deleted asset.
    const currentDist = (session.taskProgress?.assetDistribution?.answers?.distributions ?? {}) as DistributionMap;
    if (currentDist[assetId]) {
      const cleanedDist = removeAssetDistribution(currentDist, assetId);
      await updateTaskProgress('assetDistribution', { distributions: cleanedDist });
    }

    await persistAssets(updatedAssets);
  };

  const handleRemoveAttachedDocument = async (assetId: string, documentIndex: number) => {
    const asset = assets.find((a) => a.id === assetId);
    const docToRemove = asset?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame189] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedAssets = assets.map((asset) => {
      if (asset.id !== assetId) return asset;
      return {
        ...asset,
        documents: normalizeDocuments(asset.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    await persistAssets(updatedAssets);
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame189] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    navigate('/frame-191');
  };

  const handleBack = () => {
    navigate('/frame-188');
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Other Financial Assets" />

        <SubHeadline>
          Add your other financial assets including life insurance, annuities, trusts, and
          business interests. This information will help your loved ones locate and manage these
          assets.
        </SubHeadline>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {ASSET_TYPES.map((type) => (
            <button
              key={type.key}
              onClick={() => handleOpenModal(type.key)}
              className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <type.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-stone-800 leading-tight">{type.label}</h3>
                  <p className="text-sm text-stone-600 mt-1">{type.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {assets.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-medium text-stone-800 mb-4">
              Your Assets ({assets.length})
            </h2>

            <div
              className={`space-y-4 ${assets.length >= 3 ? 'max-h-56 min-h-0 overflow-y-auto' : ''}`}
            >
              {assets.map((asset) => {
                const assetType = ASSET_TYPES.find((type) => type.key === asset.type);
                const AssetIcon = assetType?.Icon || BankIcon;

                const activeBeneficiaries =
                  asset.beneficiaries && asset.beneficiaries.length > 0
                    ? asset.beneficiaries.filter((b) =>
                        contacts.some((c) => c.id === b.contactId && c.active !== false)
                      )
                    : [];

                return (
                  <div
                    key={asset.id}
                    className="bg-white rounded-lg border border-neutral-200 p-4"
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                        <AssetIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-stone-800 break-words">{asset.name}</div>

                          <div className="text-sm text-stone-500 mt-1 break-words">
                            {asset.institution}
                            {asset.policyNumber && ` • Policy: ${asset.policyNumber}`}
                          </div>

                          {activeBeneficiaries.length > 0 && (
                            <div className="text-sm text-stone-600 mt-2 break-words">
                              Beneficiaries: {activeBeneficiaries.map((b) => b.name).join(', ')}
                            </div>
                          )}

                          {asset.notes && (
                            <div className="text-sm text-stone-600 mt-2 break-words">{asset.notes}</div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                        <button
                          onClick={() => handleEditAsset(asset)}
                          className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAsset(asset.id)}
                          className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {asset.documents && asset.documents.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="text-sm text-cyan-700 font-medium">
                          Attached Documents ({asset.documents.length})
                        </div>

                        <div className="space-y-2">
                          {normalizeDocuments(asset.documents).map((doc, index) => (
                            <div
                              key={`${asset.id}-doc-${doc.id}-${index}`}
                              className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <button
                                type="button"
                                onClick={() => openDocument(doc)}
                                className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                              >
                                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                <span
                                  className="text-sm font-medium text-stone-800 truncate"
                                  title={doc.name}
                                >
                                  {doc.name}
                                </span>
                              </button>

                              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                <button
                                  type="button"
                                  onClick={() => downloadDocument(doc)}
                                  className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                >
                                  Download
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveAttachedDocument(asset.id, index)}
                                  className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                  aria-label={`Remove ${doc.name}`}
                                  title="Remove document"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </ResponsiveContent>

      <ResponsiveFooter
        title="Inventory Your Assets"
        subtitle="Add other financial assets"
        progressPercent={50}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />

      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingAssetId ? 'Edit Asset' : 'Add Asset'}
        okText={editingAssetId ? 'Update Asset' : 'Add Asset'}
        cancelText="Cancel"
        onOk={handleSave}
        onCancel={() => {
          setShowModal(false);
          resetForm();
        }}
      >
        <div className="space-y-6 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Asset Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., Term Life Insurance"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Institution/Provider *
            </label>
            <input
              type="text"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., State Farm Insurance"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Policy/Account Number *
            </label>
            <input
              type="text"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Policy or account number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Beneficiaries
            </label>
            <p className="text-xs text-stone-600 mb-2 italic">
              Note: Beneficiary designations are governed by the financial institution. Please make
              sure the information here matches what&apos;s on file.
            </p>

            <div className="space-y-2">
              {beneficiaryIds.map((beneficiaryId, index) => (
                <div key={index} className="flex items-center gap-2">
                  <select
                    value={beneficiaryId}
                    onChange={(e) => {
                      if (e.target.value === '__add_new_contact__') {
                        setShowManualContactModal(true);
                        return;
                      }
                      const newBeneficiaries = [...beneficiaryIds];
                      newBeneficiaries[index] = e.target.value;
                      setBeneficiaryIds(newBeneficiaries);
                    }}
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-md"
                  >
                    <option value="">Select a beneficiary</option>
                    {contacts
                      .filter((c) => c.active !== false)
                      .map((contact) => (
                        <option key={contact.id} value={contact.id}>
                          {contact.firstName} {contact.lastName}{' '}
                          {contact.relationship
                            ? `(${contact.relationship.charAt(0).toUpperCase()}${contact.relationship.slice(1)})`
                            : ''}
                        </option>
                      ))}
                    <option value="__add_new_contact__">+ Add new contact...</option>
                  </select>

                  <button
                    onClick={() => {
                      const newBeneficiaries = beneficiaryIds.filter((_, i) => i !== index);
                      setBeneficiaryIds(newBeneficiaries);
                    }}
                    className="text-red-600 hover:text-red-700 px-3 py-2"
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                onClick={() => setBeneficiaryIds([...beneficiaryIds, ''])}
                className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
              >
                + Add Beneficiary
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Any additional information"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Upload Documents
            </label>
            <FileUpload
              onUpload={async (newFiles) => {
                if (!newFiles.length) return;

                setIsUploadingDocuments(true);

                try {
                  const uploadedDocs = await Promise.all(
                    newFiles.map((file) => documentService.upload(file))
                  );

                  setDocuments((prev) => [...prev, ...uploadedDocs]);
                } catch (error) {
                  console.error('[Frame189] Failed to upload document(s):', error);
                  alert('Failed to upload one or more documents.');
                } finally {
                  setIsUploadingDocuments(false);
                }
              }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              multiple
            />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <ul className="mt-2 space-y-2">
                {documents.map((doc, index) => (
                  <li
                    key={`${doc.id}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600 bg-stone-50 p-2 border border-stone-200 rounded"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0" />

                      <span className="text-sm font-medium text-cyan-700 hover:text-cyan-800 truncate" title={doc.name}>
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>

                      <button
                        onClick={() => removeDocumentFromForm(index)}
                        className="ml-2 text-red-600 hover:text-red-700 text-xs shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      </Modal>

      <ManualContactModal
        open={showManualContactModal}
        onClose={() => setShowManualContactModal(false)}
        onSubmit={handleManualContactSubmit}
        initialData={{ relationship: 'beneficiary' }}
      />
    </ResponsiveContainer>
  );
};

export default Frame189;